import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ImmomioIconName, InputType, ITheme, IUserData, MasterdataChangeType } from '../../models';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { getMasterDataChange, getPersonalData, getTheme } from '../../+state';
import { filter, map, take } from 'rxjs';
import {
  ibanValidator,
  nameValidator,
  phoneNumberValidator,
  phonePrefixValidator,
  zipCodeValidator,
} from '../../utils';

@UntilDestroy()
@Directive()
export abstract class UserEditFormBaseComponent {
  @Output() formSubmitted = new EventEmitter();
  @Input() public masterDataChangeType: MasterdataChangeType;
  public inputType = InputType;
  public immomioIconName = ImmomioIconName;
  public paymentNotice: string;
  public theme: ITheme;

  nameEditForm: FormGroup;
  birthDateEditForm: FormGroup;
  addressEditForm: FormGroup;
  phoneEditForm: FormGroup;
  emailEditForm: FormGroup;
  paymentEditForm: FormGroup;

  constructor(
    protected fb: FormBuilder,
    protected store: Store
  ) {
    this.initializeForms();
    this.store
      .select(getPersonalData)
      .pipe(take(1))
      .subscribe(personalData => this.prefillForms(personalData));

    this.store
      .select(getMasterDataChange)
      .pipe(
        take(1),
        map(
          masterDataChange =>
            masterDataChange?.selectedChangeConfiguration?.externalPaymentData?.notice
        )
      )
      .subscribe(notice => {
        this.paymentNotice = notice;
      });

    this.store
      .select(getTheme)
      .pipe(
        filter(theme => theme !== null),
        take(1)
      )
      .subscribe(theme => {
        this.theme = theme;
      });
  }

  getAttachment(event): void {
    if (event) {
      this.nameEditForm.patchValue({ name: { attachments: event } });
    } else {
      this.nameEditForm.get('name.attachments')?.patchValue(event);
    }
  }

  private initializeForms(): void {
    this.nameEditForm = this.fb.group({
      name: this.fb.group({
        name: ['', [Validators.required, nameValidator()]],
        lastName: ['', [Validators.required, nameValidator()]],
        attachments: ['', [Validators.required]],
      }),
    });

    this.birthDateEditForm = this.fb.group({
      dateOfBirth: this.fb.group({
        dateOfBirth: ['', [Validators.required]],
      }),
    });

    this.addressEditForm = this.fb.group({
      address: this.fb.group({
        street: ['', [Validators.required]],
        houseNumber: ['', [Validators.required]],
        zipCode: [
          '',
          [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(5),
            zipCodeValidator(),
          ],
        ],
        city: ['', [Validators.required]],
      }),
    });

    this.phoneEditForm = this.fb.group({
      countryCode: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(4),
          phonePrefixValidator(),
        ],
      ],
      prefix: ['', [Validators.required, phoneNumberValidator()]],
      phoneNumber: ['', [Validators.required, phoneNumberValidator()]],
    });

    this.emailEditForm = this.fb.group({
      email: this.fb.group({
        email: ['', [Validators.required, Validators.email]],
      }),
    });

    this.paymentEditForm = this.fb.group({
      payment: this.fb.group({
        iban: ['', [Validators.required, ibanValidator()]],
        accountOwner: ['', [Validators.required, nameValidator()]],
        issuedAt: [new Date(), [Validators.required]],
        selectedContracts: [[], [Validators.required]],
        hasAcceptedSepaMandate: [false, [Validators.required]],
        notice: [''],
      }),
    });
  }

  private prefillForms(data: IUserData | null): void {
    if (!data) return;

    this.nameEditForm.patchValue({
      name: {
        name: data.firstName,
        lastName: data.name,
      },
    });

    this.birthDateEditForm.patchValue({
      dateOfBirth: { dateOfBirth: data.birthDate },
    });

    this.emailEditForm.patchValue({
      email: { email: data.contactEmail },
    });

    if (data.address) {
      this.addressEditForm.patchValue({
        address: {
          street: data.address?.street,
          houseNumber: data.address?.houseNumber,
          zipCode: data.address?.zipCode,
          city: data.address?.city,
        },
      });
    }
  }
}
